import { useCallback } from 'react';
import { toast } from 'sonner';
import { WriteRefusedError } from '../../../lib/apiFetchJson';
import type { EnrollmentItem } from '@shared/flow/enrollment';
import type { PlayOption } from '@shared/flow/play-catalog';

/**
 * The "change this item's play" sequence, extracted verbatim out of
 * `AssignedRow.tsx`'s inline `PlayPicker` `onAssign` handler so the V2 rail's
 * `LeadExpandedBody` can mount the SAME behavior instead of re-deriving it
 * (D41, terra-enroll-queue-rail-v2 plan — one hook, two consumers, the same
 * shape D6 uses for the step editor).
 *
 * What the sequence does, and why each leg exists, is unchanged from the
 * comments that sat above the inline handler:
 *
 * - **U7b (reassign-drafts fix, Codex #3)** — FIRST clear any local
 *   step-override drafts for this item synchronously (they have no server
 *   counterpart to race), THEN drain the item's pending autosave
 *   (`onFlushSave` — a no-op if nothing is pending/in-flight) so a stale
 *   trailing-edge write can never fire AFTER the reassign PATCH and resurrect
 *   the old play's subject/body/sender onto the row the server just reset.
 *   Only once that flush has settled does it call `onReassign`, then — once
 *   THAT resolves (the same await-invalidation sequencing `UnassignedRow`
 *   uses) — fire `requestQueueFlash` (accepted degrade if the destination
 *   card is collapsed, Open Question, plan U7). A flush failure must not
 *   block the reassign itself — the rep is deliberately moving away from that
 *   draft — so it's swallowed, same posture as the unmount-flush path in
 *   `EnrollmentItemEditor.tsx`.
 *
 * - **Reflection fix (2026-09-01): also UNMOUNT the item editor.**
 *   `EnrollmentItemEditor` seeds its `useDraftState` on `item.id`, which a
 *   reassign does NOT change — so in the By Contact grouping (where
 *   `ContactQueueRow` is keyed by item id and therefore never remounts on a
 *   play change) the editor would keep displaying the OLD play's
 *   subject/body/sender that U7's server-side reset just nulled, and the next
 *   keystroke's autosave would write them straight back onto the NEW play.
 *   Same class of bug U7b fixed for the step-override store, same remedy:
 *   drop the stale local copy. Closing unmounts the editor, so reopening
 *   reseeds from the reset row. (`requestQueueFlash` is called WITHOUT
 *   `openEditorAfterAssign`, so nothing reopens it behind the rep's back.)
 *
 * - **Reflection fix (2026-09-01, failure-restore + error surface)** — a
 *   REJECTED `onReassign` is handled: the item never actually moved
 *   server-side (a failed PATCH writes nothing), so the swept step-override
 *   drafts captured above are restored via the undo closure, and the rep is
 *   told why via a toast carrying the server's own message
 *   (`PatchQueueItemError.message`, the same surface-the-real-reason posture
 *   as `queue-group-send.ts`'s save-failure toast). The rejection handler is
 *   scoped to exactly the `onReassign` leg (the two-callback `.then`) so a
 *   hypothetical throw inside `requestQueueFlash` itself can never trigger a
 *   bogus restore.
 *
 * **What this hook deliberately does NOT own: the picker's open state.** The
 * plan's stated fallback (D41 / "Deferred to Implementation") applies — the
 * sequence is entangled with each consumer's OWN local `editorOpen` /
 * `reassignOpen` `useState`, so those stay declared in the consumer and are
 * handed in here as the two `setState` inputs below. That keeps the ORDER of
 * `setEditorOpen(false)` → flush/reassign → `setReassignOpen(false)`
 * byte-identical to the pre-extraction handler (the close-the-picker call
 * fires synchronously, NOT after the promise chain), which is what several
 * V1 tests key on.
 */
export type UseReassignPlayInput = {
  /**
   * Only `id` and `contact_id` are read (the item id the PATCH targets, and
   * the contact id the post-move flash keys on). Typed as a `Pick` rather
   * than the whole row so a consumer holding a narrower projection can still
   * use the hook; a full `EnrollmentItem` satisfies it.
   */
  item: Pick<EnrollmentItem, 'id' | 'contact_id'>;
  /** See `AssignedRow`'s `onFlushSave` prop — drains this item's pending per-item autosave. */
  onFlushSave: (itemId: number) => Promise<void>;
  /** See `AssignedRow`'s `onClearStepOverrides` prop — sweeps local per-step drafts and returns the undo closure that restores them. */
  onClearStepOverrides: (itemId: number) => () => void;
  /** See `AssignedRow`'s `onReassign` prop — the existing `assignPlay` mutation, framed as a move because the item is already assigned. */
  onReassign: (itemId: number, play: PlayOption) => void | Promise<void>;
  /** See `AssignedRow`'s `requestQueueFlash` prop — optional, and `undefined` in the full-screen Review mount. */
  requestQueueFlash?: (contactId: string, opts?: { autoOpenPicker?: boolean; openEditorAfterAssign?: boolean }) => void;
  /**
   * Closes the consumer's item editor (the stale-draft unmount above).
   * Declared as a plain boolean setter, not `Dispatch<SetStateAction<boolean>>`,
   * so a consumer that does not hold the state in a bare `useState` can pass
   * any equivalent closer; a `useState` setter is assignable to it.
   */
  setEditorOpen: (open: boolean) => void;
  /** Closes the consumer's inline `PlayPicker` once a play has been picked. Same typing rationale as `setEditorOpen`. */
  setReassignOpen: (open: boolean) => void;
};

export type UseReassignPlayResult = {
  /** Drop-in for the `PlayPicker`'s `onAssign` prop. Stable across renders as long as the inputs are. */
  reassign: (play: PlayOption) => void;
};

export function useReassignPlay({
  item,
  onFlushSave,
  onClearStepOverrides,
  onReassign,
  requestQueueFlash,
  setEditorOpen,
  setReassignOpen,
}: UseReassignPlayInput): UseReassignPlayResult {
  const itemId = item.id;
  const contactId = item.contact_id;

  const reassign = useCallback(
    (play: PlayOption) => {
      const restoreStepOverrides = onClearStepOverrides(itemId);
      setEditorOpen(false);
      void onFlushSave(itemId)
        .catch(() => {})
        .then(() => onReassign(itemId, play))
        .then(
          () => {
            requestQueueFlash?.(contactId);
          },
          (err: unknown) => {
            restoreStepOverrides();
            // apiFetch already announced a refusal.
            if (err instanceof WriteRefusedError) return;
            toast.error(err instanceof Error ? err.message : 'Failed to change play');
          },
        );
      setReassignOpen(false);
    },
    [itemId, contactId, onClearStepOverrides, onFlushSave, onReassign, requestQueueFlash, setEditorOpen, setReassignOpen],
  );

  return { reassign };
}
